import { useMemo, useState } from "react";
import { useMutation, useQuery, type TypedDocumentNode } from "@apollo/client";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { useErrorBanner } from "@/providers/ErrorBannerProvider/useErrorBanner";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useConfigurationAccess } from "../access";
import { CONFIGURATION_SECTIONS_QUERY } from "../queries";
import type { ConfigurationSectionsQueryResponse, ConfigurationSectionsQueryVariables } from "../queries";
import type {
  UpdateSectionCoverageRoleResponse,
  UpdateSectionCoverageRoleVariables,
  UpdateSectionPlayerEngagementConsultantResponse,
  UpdateSectionRecruiterResponse
} from "../mutations";
import type { CoverageGroupMember, CoverageGroupRow, CoverageRoleKind, CoverageStaff } from "../types";
import AssignCoverageRoleDialog from "./AssignCoverageRoleDialog";
import ConfigurationLoadError from "./ConfigurationLoadError";
import ConfirmCoverageChangeDialog from "./ConfirmCoverageChangeDialog";
import CoverageGroupTabs, { useCoverageGroupBy } from "./CoverageGroupTabs";
import GroupedCoverageTable from "./GroupedCoverageTable";
import {
  CONFIGURATION_LIST_FIRST,
  filterCoverageGroups,
  groupCoverageByPerson,
  groupCoverageBySection,
  isCoverageListTruncated
} from "./groupCoverage";

type CoverageMutationData = UpdateSectionRecruiterResponse | UpdateSectionPlayerEngagementConsultantResponse;

type SharedCoverageRoleTableProps = {
  personColumnHeader: string;
  consultantTabLabel: string;
  searchPlaceholder: string;
  assignHeadline: string;
  staffLabel: string;
  addPersonActionLabel: string;
  clearHeadline: string;
  tableTestId: string;
};

type CoverageRoleTableProps = SharedCoverageRoleTableProps &
  (
    | {
        kind: "recruiter";
        mutation: TypedDocumentNode<UpdateSectionRecruiterResponse, UpdateSectionCoverageRoleVariables>;
      }
    | {
        kind: "playerEngagement";
        mutation: TypedDocumentNode<
          UpdateSectionPlayerEngagementConsultantResponse,
          UpdateSectionCoverageRoleVariables
        >;
      }
  );

function coveragePayload(
  kind: CoverageRoleKind,
  data: CoverageMutationData | null | undefined
): UpdateSectionCoverageRoleResponse | undefined {
  if (!data) return undefined;
  if (kind === "recruiter") {
    return "updateSectionRecruiter" in data ? data.updateSectionRecruiter : undefined;
  }
  return "updateSectionPlayerEngagementConsultant" in data ? data.updateSectionPlayerEngagementConsultant : undefined;
}

function mutationErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export default function CoverageRoleTable({
  kind,
  personColumnHeader,
  consultantTabLabel,
  searchPlaceholder,
  assignHeadline,
  staffLabel,
  addPersonActionLabel,
  clearHeadline,
  mutation,
  tableTestId
}: CoverageRoleTableProps) {
  const { loaded, canView } = useConfigurationAccess();
  const { Menu, setMenu } = useTableRowMenu();
  const { setError } = useErrorBanner();
  const groupBy = useCoverageGroupBy();
  const [assignTarget, setAssignTarget] = useState<{
    sectionId?: string;
    sectionName?: string;
    currentPerson?: CoverageStaff | null;
    person?: CoverageStaff | null;
  } | null>(null);
  const [rowToClear, setRowToClear] = useState<CoverageGroupMember | null>(null);

  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search",
    debounceMs: 300
  });

  const { data, loading, error, refetch } = useQuery<
    ConfigurationSectionsQueryResponse,
    ConfigurationSectionsQueryVariables
  >(CONFIGURATION_SECTIONS_QUERY, {
    skip: !loaded || !canView,
    variables: {
      input: {
        search: undefined
      },
      first: CONFIGURATION_LIST_FIRST
    }
  });

  const [updateCoverage, { loading: saving }] = useMutation<CoverageMutationData, UpdateSectionCoverageRoleVariables>(
    mutation,
    {
      refetchQueries: [CONFIGURATION_SECTIONS_QUERY],
      awaitRefetchQueries: true
    }
  );

  const sections = data?.crmSections.nodes ?? [];
  const groups = useMemo(() => {
    const sectionNodes = data?.crmSections.nodes ?? [];
    const grouped =
      groupBy === "section" ? groupCoverageBySection(sectionNodes, kind) : groupCoverageByPerson(sectionNodes, kind);
    return filterCoverageGroups(grouped, debouncedSearch);
  }, [data, debouncedSearch, groupBy, kind]);

  const openAssign = (target: {
    sectionId?: string;
    sectionName?: string;
    currentPerson?: CoverageStaff | null;
    person?: CoverageStaff | null;
  }) => {
    setAssignTarget(target);
  };

  const handleAddGroup = (group: CoverageGroupRow) => {
    openAssign({
      sectionId: group.lockedSectionId,
      sectionName: group.lockedSectionName,
      currentPerson: group.members[0]?.person ?? null,
      person: group.lockedPerson ?? null
    });
  };

  const handleAddMember = (member: CoverageGroupMember) => {
    openAssign({
      sectionId: member.sectionId,
      sectionName: member.sectionName,
      currentPerson: member.person
    });
  };

  const handleAssign = async ({ sectionId, backofficeUserId }: { sectionId: string; backofficeUserId: string }) => {
    try {
      const result = await updateCoverage({
        variables: { input: { sectionId, backofficeUserId } }
      });
      const payload = coveragePayload(kind, result.data);
      if (payload?.success) {
        showSnackbar(payload.message);
        setAssignTarget(null);
        return;
      }
      setError(payload?.message || "Couldn't save this assignment.");
    } catch (caught) {
      setError(mutationErrorMessage(caught, "Couldn't save this assignment."));
    }
  };

  const handleClear = async () => {
    if (!rowToClear) return;

    try {
      const result = await updateCoverage({
        variables: { input: { sectionId: rowToClear.sectionId, backofficeUserId: null } }
      });
      const payload = coveragePayload(kind, result.data);
      if (payload?.success) {
        showSnackbar(payload.message);
        setRowToClear(null);
        return;
      }
      setError(payload?.message || "Couldn't clear this assignment.");
    } catch (caught) {
      setError(mutationErrorMessage(caught, "Couldn't clear this assignment."));
    }
  };

  const addActionLabel = groupBy === "section" ? addPersonActionLabel : "Add Section";

  return (
    <>
      {!error ? <CoverageGroupTabs consultantTabLabel={consultantTabLabel} onGroupByChange={clearSearch} /> : null}
      <Menu />
      <div className="pt-12" />
      {error ? (
        <ConfigurationLoadError onRetry={() => refetch()} />
      ) : (
        <GroupedCoverageTable
          groups={groups}
          loading={loading}
          truncated={isCoverageListTruncated(data?.crmSections)}
          groupBy={groupBy}
          groupColumnHeader={groupBy === "section" ? "Section" : personColumnHeader}
          addActionLabel={addActionLabel}
          memberAddActionLabel={addPersonActionLabel}
          removeActionLabel="Clear"
          searchPlaceholder={groupBy === "section" ? searchPlaceholder : `Search ${personColumnHeader}s`}
          searchValue={searchValue}
          hasActiveFilters={!!debouncedSearch}
          onSearchChange={setSearchValue}
          onSearchClear={clearSearch}
          tableTestId={tableTestId}
          emptyTitle="No sections found."
          emptyIcon="domain"
          setMenu={setMenu}
          onAdd={handleAddGroup}
          onAddMember={handleAddMember}
          onRemoveMember={setRowToClear}
        />
      )}
      <AssignCoverageRoleDialog
        open={Boolean(assignTarget)}
        onClose={() => setAssignTarget(null)}
        onAssign={handleAssign}
        saving={saving && Boolean(assignTarget)}
        headline={assignHeadline}
        staffLabel={staffLabel}
        kind={kind}
        sections={sections}
        sectionId={assignTarget?.sectionId}
        sectionName={assignTarget?.sectionName}
        currentPerson={assignTarget?.currentPerson}
        person={assignTarget?.person}
      />
      <ConfirmCoverageChangeDialog
        open={Boolean(rowToClear)}
        onClose={() => setRowToClear(null)}
        onConfirm={handleClear}
        saving={saving && Boolean(rowToClear)}
        headline={clearHeadline}
        body={
          rowToClear
            ? `Clear ${rowToClear.person?.name ?? "this assignment"} from ${rowToClear.sectionName}? This leaves the section unassigned.`
            : ""
        }
        confirmLabel="Clear"
      />
    </>
  );
}
